import { ICellRendererAngularComp } from 'ag-grid-angular';
import { CommonModule } from '@angular/common';
import { Component, inject, TemplateRef, ViewChild, NgZone } from '@angular/core';
import { Router } from '@angular/router';
import { JoinTenant } from '@app/+state/access-requests';
import { TenantActionItem, TenantRow } from '@app/+state/user-data/user.model';
import { getTenantActionMenu, leaveOrgDialogConfig, openDialogWrapper$ } from '@app/v2/shared/utils';
import { LeaveOrgModalComponent } from '@app/v2/standalone/shared/leave-org-modal/leave-org-modal.component';
import { AccessibleTenant, Role, SelectedTenant, UserDataFacade } from '@ra-state';
import {
  BADGE_STATUS_COLORS,
  BadgePosition,
  BadgeSize,
  DialogService,
  IDialogConfig,
  RaUiBadgeModule,
  RaUiButtonModule,
  RaUiMenuModule,
  RaUiTooltipModule,
  TooltipPosition,
} from '@ra-web-tech-ui-toolkit/components';
import { tap } from 'rxjs';

@Component({
  selector: 'app-ag-organization-actions-menu-renderer',
  standalone: true,
  imports: [CommonModule, RaUiBadgeModule, RaUiButtonModule, RaUiMenuModule, RaUiTooltipModule, LeaveOrgModalComponent],
  templateUrl: './ag-organization-actions-menu-renderer.component.html',
  styleUrl: './ag-organization-actions-menu-renderer.component.scss',
})
export class AgOrganizationActionsMenuRendererComponent implements ICellRendererAngularComp {
  @ViewChild('leaveOrgModal') leaveOrgModal: TemplateRef<any>;
  // Reference to the raUiMenuTriggerFor directive to force a position update after open.
  // ag-grid-angular (v34) is compiled for Angular 17 and its ViewContainerRef context
  // does not properly integrate with Angular 20's afterNextRender, causing the CDK overlay
  // to initially render at (0,0). Calling updatePosition() in the next macrotask corrects this.
  @ViewChild('menuTrigger') menuTrigger: any;
  badgeConfig = {
    position: BadgePosition.After,
    color: BADGE_STATUS_COLORS.Status,
    size: BadgeSize.Medium,
  };
  tooltipConfig = { maxWidth: 400, position: TooltipPosition.Left };
  router = inject(Router);
  ngZone = inject(NgZone);
  params: any;
  menuItems: TenantActionItem[] = [];

  userDataFacade = inject(UserDataFacade);
  dialogSvc = inject(DialogService);
  openDialogWrapper$ = openDialogWrapper$;
  isFromSubOrgPage: boolean;
  selectedTenant: SelectedTenant;
  selectedTenant$ = this.userDataFacade.selectedTenant$.pipe(
    tap((selectedTenant) => {
      this.selectedTenant = selectedTenant;
    }),
  );

  agInit(params: any): void {
    this.params = params;
    this.isFromSubOrgPage = this.params.context.componentParent.isParentOrganization === false;
    this.getActionMenu();
  }

  refresh(): boolean {
    return false;
  }

  getActionMenu(): void {
    this.menuItems = getTenantActionMenu(this.params.data, this.isFromSubOrgPage).filter((item) => {
      if (this.isFromSubOrgPage) {
        return item.label !== 'Sub-organizations';
      }
      return true;
    });
  }

  onKebabClick(): void {
    // ag-grid-angular (v34) uses an Angular 17-compiled ViewContainerRef which doesn't
    // integrate with Angular 20's afterNextRender. The CDK overlay positions itself in
    // afterNextRender but fires before the cell's layout is resolvable in this context,
    // leaving the pane at (0,0). Forcing updatePosition() in the next macrotask corrects it.
    this.ngZone.runOutsideAngular(() => {
      setTimeout(() => {
        if (this.menuTrigger?.overlayRef?.hasAttached()) {
          this.menuTrigger.overlayRef.updatePosition();
        }
      }, 0);
    });
  }
  restoreOrganization($event): void {
    const payload: AccessibleTenant = {
      id: $event.id,
      name: this.isFromSubOrgPage ? `${this.selectedTenant.name}/${$event.name}` : $event.name,
      shortName: $event.name,
      parentId: this.isFromSubOrgPage ? this.selectedTenant.id : undefined,
      logoUrl: $event.logoUrl,
    };
    this.userDataFacade.unarchiveTenant(payload);
  }

  onMenuItemClick($event): void {
    if ($event.label === 'Leave...') {
      this.leaveOrganization();
      return;
    }
    const routePath = $event.routePath.includes(':orgId')
      ? $event.routePath.replace(':orgId', this.params.data.id)
      : $event.routePath;
    this.userDataFacade.tenantChange(this.params.data.id, routePath);
  }

  get IsOwner(): boolean {
    return this.params.data.role === Role.Owner;
  }

  leaveOrganization(): void {
    const config: IDialogConfig = {
      ...leaveOrgDialogConfig,
      title: `Leave ${this.params.data.name}`,
      contentProjection: { content: this.leaveOrgModal },
    };

    this.openDialogWrapper$(this.dialogSvc, config).subscribe((event: any) => {
      if (event.label === 'Leave') {
        this.userDataFacade.leaveTenant({
          tenantId: this.params.data.id,
          tenantName: this.params.data.name,
          selectedTenantId: this.selectedTenant?.id,
          isLeavingCurrentTenant: this.params.data.id === this.selectedTenant?.id,
          tenantType: this.isFromSubOrgPage ? 'sub-organization' : 'organization',
          isFromSettingsPage: false,
        });
      }
    });
  }

  joinSubOrg(subOrg: TenantRow): void {
    const joinTenantRequest: JoinTenant = {
      requestBody: {
        resourceId: subOrg.id,
        tenantId: subOrg.id,
      },
      toastBody: {
        tenantName: subOrg.name,
      },
    };
    this.userDataFacade.accessRequests.joinTenant(joinTenantRequest, false);
  }
}
